// src/components/AddMovForm.jsx
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Modal, Button, Form, ListGroup, Spinner } from "react-bootstrap";
import "animate.css";
import "../css/addclienteform.css";

import { postMov } from "../helpers/rutaMovs";
import { getHoms } from "../helpers/rutaHoms";
import { getClienteByCodigo, buscarClientes } from "../helpers/rutaClientes";

/* ---------------- utils ---------------- */
const todayLocalISO = () => {
  const d = new Date();
  const local = new Date(d.getTime() - d.getTimezoneOffset() * 60000);
  return local.toISOString().slice(0, 10);
};

const safeParse = (raw) => {
  if (!raw) return null;

  const s = String(raw).trim();
  if (!s) return null;

  // Si parece JSON, intentamos parsear; si no, devolvemos string.
  const looksJson = s.startsWith("{") || s.startsWith("[") || s.startsWith('"');
  if (!looksJson) return s;

  try {
    return JSON.parse(s);
  } catch {
    return s;
  }
};

const getUsuarioLogueado = () => {
  // ajustá acá si tu login usa otra key
  const raw =
    localStorage.getItem("currentUser") ||
    localStorage.getItem("user") ||
    localStorage.getItem("usuario") ||
    localStorage.getItem("userDisplay");

  const parsed = safeParse(raw);

  // Si es string plano
  if (typeof parsed === "string") return parsed.trim();

  // Si es objeto
  if (parsed && typeof parsed === "object") {
    const nombre = String(
      parsed?.nombres ||
        parsed?.username ||
        parsed?.usuario ||
        parsed?.name ||
        parsed?.user?.usuario ||
        parsed?.user?.username ||
        ""
    ).trim();

    if (nombre) return nombre;
  }

  // fallback por si hay un displayName guardado separado
  return String(localStorage.getItem("userDisplay") || "").trim();
};

/* ---------------- initial form ---------------- */
const initialBase = {
  codmov: "",
  codcli: "",
  codhom: "",
  fecmov: todayLocalISO(),
  leyenda: "",
  usuario1: "", // string Fox
};

export default function AddMovForm({ setShow }) {
  // precarga usuario al iniciar (así se ve al toque)
  const [formValues, setFormValues] = useState(() => ({
    ...initialBase,
    usuario1: getUsuarioLogueado(),
  }));

  const [showSuccess, setShowSuccess] = useState(false);

  const [homs, setHoms] = useState({ data: [], loading: true });

  const [ui, setUi] = useState({
    clienteDesc: "",
    homDesc: "",
  });

  const setField = useCallback((name, value) => {
    setFormValues((p) => ({ ...p, [name]: value }));
  }, []);

  /* ---------------- usuario logueado -> usuario1 ---------------- */
  useEffect(() => {
    // si ya vino cargado, no lo pisamos
    if (String(formValues.usuario1 || "").trim()) return;

    const nombre = getUsuarioLogueado();
    if (nombre) setField("usuario1", nombre);
  }, [formValues.usuario1, setField]);

  const validate = () => {
    const codcli = Number(formValues.codcli || 0);
    const codhom = Number(formValues.codhom || 0);
    const usuario1 = String(formValues.usuario1 || "").trim();
    const leyenda = String(formValues.leyenda || "").trim();

    if (codcli <= 0) return "Seleccioná un cliente (no puede ser 0).";
    if (codhom <= 0) return "Seleccioná un HOM.";
    if (!formValues.fecmov) return "Ingresá la fecha.";
    if (!leyenda) return "Ingresá la leyenda.";
    if (!usuario1) return "No se detectó el usuario logueado.";
    return "";
  };

  /* ---------------- HOMs ---------------- */
  useEffect(() => {
    getHoms()
      .then((list) => setHoms({ data: Array.isArray(list) ? list : [], loading: false }))
      .catch(() => setHoms({ data: [], loading: false }));
  }, []);

  const homMap = useMemo(() => {
    const map = {};
    (homs.data || []).forEach((h) => (map[String(h.codhom)] = h?.hom || ""));
    return map;
  }, [homs.data]);

  useEffect(() => {
    setUi((p) => ({ ...p, homDesc: homMap[String(formValues.codhom)] || "" }));
  }, [formValues.codhom, homMap]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setField(name, value);
  };

  const handleHom = (e) => setField("codhom", e.target.value);

  /* ---------------- cliente por código ---------------- */
  const [buscandoCod, setBuscandoCod] = useState(false);
  const reqSeqCod = useRef(0);

  const fetchClienteByCod = useCallback(async (codcli) => {
    const mySeq = ++reqSeqCod.current;

    setBuscandoCod(true);
    try {
      const resp = await getClienteByCodigo(codcli);
      if (mySeq !== reqSeqCod.current) return;

      if (resp?.ok === false) {
        setUi((p) => ({ ...p, clienteDesc: resp?.error || "No autorizado" }));
        return;
      }

      const cli = resp?.cliente ?? resp;
      setUi((p) => ({ ...p, clienteDesc: cli?.razonsocial || "No encontrado" }));
    } catch (err) {
      if (mySeq !== reqSeqCod.current) return;

      const status = err?.response?.status;
      const msg =
        status === 401 || status === 403
          ? "Sin permiso (rol)"
          : status === 404
          ? "No encontrado"
          : "Error consultando cliente";

      setUi((p) => ({ ...p, clienteDesc: msg }));
    } finally {
      if (mySeq === reqSeqCod.current) setBuscandoCod(false);
    }
  }, []);

  /* ---------------- modal buscar cliente (0 -> modal) ---------------- */
  const [showCliModal, setShowCliModal] = useState(false);
  const [qCli, setQCli] = useState("");
  const [cliOpts, setCliOpts] = useState([]);
  const [loadingCli, setLoadingCli] = useState(false);
  const [selIdx, setSelIdx] = useState(0);
  const reqSeqQ = useRef(0);
  const inputRef = useRef(null);

  const openCliModal = useCallback(() => {
    setShowCliModal(true);
    setQCli("");
    setCliOpts([]);
    setSelIdx(0);
    setTimeout(() => inputRef.current?.focus(), 50);
  }, []);

  const closeCliModal = useCallback(() => setShowCliModal(false), []);

  const buscarCliente = useCallback(
    async (rawOverride) => {
      const raw = String(rawOverride ?? formValues.codcli ?? "").trim();

      // ✅ Regla Fox: vacío o "0" -> modal
      if (raw === "" || raw === "0") {
        openCliModal();
        return;
      }

      const codcli = Number(raw);
      if (!Number.isFinite(codcli) || codcli <= 0) {
        openCliModal();
        return;
      }

      await fetchClienteByCod(codcli);
    },
    [formValues.codcli, fetchClienteByCod, openCliModal]
  );

  // debounce búsqueda textual en modal
  useEffect(() => {
    if (!showCliModal) return;

    const q = qCli.trim();
    if (q.length < 2) {
      setCliOpts([]);
      setSelIdx(0);
      return;
    }

    const mySeq = ++reqSeqQ.current;
    const t = setTimeout(async () => {
      setLoadingCli(true);
      try {
        const list = await buscarClientes(q, 50);
        if (mySeq !== reqSeqQ.current) return;

        const arr = Array.isArray(list) ? list : [];
        setCliOpts(arr);
        setSelIdx(0);
      } catch {
        if (mySeq !== reqSeqQ.current) return;
        setCliOpts([]);
        setSelIdx(0);
      } finally {
        if (mySeq === reqSeqQ.current) setLoadingCli(false);
      }
    }, 250);

    return () => clearTimeout(t);
  }, [qCli, showCliModal]);

  const seleccionarCliente = useCallback(
    (c) => {
      if (!c) return;
      setField("codcli", String(c.codcli));
      setUi((p) => ({ ...p, clienteDesc: c.razonsocial || "" }));
      closeCliModal();
    },
    [setField, closeCliModal]
  );

  const onModalKeyDown = (e) => {
    if (e.key === "Escape") {
      e.preventDefault();
      closeCliModal();
      return;
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setSelIdx((i) => Math.min(i + 1, Math.max(0, cliOpts.length - 1)));
      return;
    }
    if (e.key === "ArrowUp") {
      e.preventDefault();
      setSelIdx((i) => Math.max(i - 1, 0));
      return;
    }
    if (e.key === "Enter") {
      e.preventDefault();
      const pick = cliOpts[selIdx] || cliOpts[0];
      if (pick) seleccionarCliente(pick);
    }
  };

  /* ---------------- submit ---------------- */
  const buildPayload = () => {
    const payload = { ...formValues };
    if (!payload.codmov) delete payload.codmov;

    payload.codcli = payload.codcli ? Number(payload.codcli) : 0;
    payload.codhom = payload.codhom ? Number(payload.codhom) : 0;
    payload.usuario1 = String(payload.usuario1 ?? "").trim();

    return payload;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const msg = validate();
    if (msg) return alert(msg);

    try {
      await postMov(buildPayload());

      setShowSuccess(true);
      setTimeout(() => {
        setShowSuccess(false);
        setFormValues({
          ...initialBase,
          fecmov: todayLocalISO(),
          usuario1: getUsuarioLogueado(), // vuelve a cargar usuario
        });
        setUi({ clienteDesc: "", homDesc: "" });
        if (typeof setShow === "function") setShow(false);
      }, 900);
    } catch (err) {
      console.error("❌ Error al crear mov:", err?.response?.status, err?.response?.data || err);
      alert("No se pudo guardar el movimiento. Revisá consola / Network.");
    }
  };

  return (
    <>
      {!homs.loading && (
        <div className="container">
          <form onSubmit={handleSubmit}>
            {/* fila 1 */}
            <div className="form-row">
              <div className="form-group mt-3 col-sm-2">
                <label>Código Mov</label>
                <input className="form-control" value={formValues.codmov} disabled placeholder="Auto" />
              </div>

              <div className="form-group mt-3 col-sm-2">
                <label>Fecha</label>
                <input
                  type="date"
                  className="form-control"
                  name="fecmov"
                  value={formValues.fecmov}
                  onChange={handleChange}
                />
              </div>

              <div className="form-group mt-3 col-sm-2">
                <label>Cod. Cliente</label>

                <div className="input-group">
                  <input
                    type="number"
                    className="form-control"
                    name="codcli"
                    value={formValues.codcli}
                    onChange={handleChange}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        buscarCliente(e.currentTarget.value);
                      }
                    }}
                    onBlur={(e) => {
                      const v = String(e.currentTarget.value ?? "").trim();
                      if (v === "0") buscarCliente(v);
                    }}
                    min="0"
                  />

                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={() => buscarCliente()}
                    title="Buscar (0 abre modal)"
                    style={{
                      width: 44,
                      height: 38,
                      padding: 0,
                      fontSize: "1rem",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    {buscandoCod ? "…" : "🔎"}
                  </button>
                </div>

                <small className="text-muted">
                  <b>0</b> y Enter buscar
                </small>
              </div>

              <div className="form-group mt-3 col-sm-6">
                <label>Cliente (Apellido y Nombre)</label>
                <input className="form-control" value={ui.clienteDesc} readOnly />
              </div>
            </div>

            {/* fila 2 */}
            <div className="form-row">
              <div className="form-group mt-3 col-sm-3">
                <label>HOM</label>
                <select className="form-control" value={formValues.codhom} onChange={handleHom} required>
                  <option value="">Elija opción</option>
                  {(homs.data || [])
                    .filter((x) => x?.activo !== false)
                    .sort((a, b) => (a.codhom ?? 0) - (b.codhom ?? 0))
                    .map((h) => (
                      <option key={h._id} value={h.codhom}>
                        {h.codhom} - {h.hom}
                      </option>
                    ))}
                </select>
              </div>

              <div className="form-group mt-3 col-sm-5">
                <label>HOM (Descripcion)</label>
                <input className="form-control" value={ui.homDesc} readOnly />
              </div>

              <div className="form-group mt-3 col-sm-4">
                <label>Usuario</label>
                <input
                  type="text"
                  className="form-control"
                  name="usuario1"
                  value={formValues.usuario1}
                  readOnly
                />
              </div>
            </div>

            {/* leyenda */}
            <div className="form-row">
              <div className="form-group mt-3 col-sm-12">
                <label>Leyendas</label>
                <textarea
                  className="form-control"
                  name="leyenda"
                  rows={3}
                  value={formValues.leyenda}
                  onChange={handleChange}
                />
              </div>
            </div>

            <button type="submit" className="btn btn-dark mt-4 mb-3" id="button">
              Guardar
            </button>
          </form>
        </div>
      )}

      {/* MODAL BUSCAR CLIENTE */}
      <Modal show={showCliModal} onHide={closeCliModal} centered backdrop="static">
        <Modal.Header closeButton>
          <Modal.Title>Buscar cliente</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <Form.Group>
            <Form.Label>Apellido / Razón social</Form.Label>
            <Form.Control
              ref={inputRef}
              value={qCli}
              placeholder="Ej: garc"
              onChange={(e) => setQCli(e.target.value)}
              onKeyDown={onModalKeyDown}
            />
            <Form.Text className="text-muted">↑ ↓ para moverte • Enter selecciona • Esc cierra</Form.Text>
          </Form.Group>

          <div className="mt-3" style={{ maxHeight: 320, overflowY: "auto" }}>
            {loadingCli && (
              <div className="d-flex align-items-center gap-2 text-muted">
                <Spinner animation="border" size="sm" /> Buscando…
              </div>
            )}

            {!loadingCli && qCli.trim().length >= 2 && !cliOpts.length && (
              <div className="text-muted">Sin resultados.</div>
            )}

            {!!cliOpts.length && (
              <ListGroup>
                {cliOpts.map((c, idx) => (
                  <ListGroup.Item
                    key={c.codcli}
                    action
                    active={idx === selIdx}
                    onClick={() => seleccionarCliente(c)}
                  >
                    <b>{c.codcli}</b> — {c.razonsocial}
                  </ListGroup.Item>
                ))}
              </ListGroup>
            )}
          </div>
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={closeCliModal}>
            Cerrar
          </Button>
          <Button
            variant="primary"
            onClick={() => seleccionarCliente(cliOpts[selIdx] || cliOpts[0])}
            disabled={!cliOpts.length}
          >
            Seleccionar
          </Button>
        </Modal.Footer>
      </Modal>

      {/* SUCCESS */}
      <Modal
        show={showSuccess}
        centered
        backdrop="static"
        keyboard={false}
        dialogClassName="animate__animated animate__fadeInDown"
      >
        <Modal.Body className="text-center">
          <div style={{ fontSize: "3rem" }}>🖐</div>
          <h5 className="mt-2">¡Movimiento generado correctamente!</h5>
        </Modal.Body>
      </Modal>
    </>
  );
}